import { commentLots, handleBlockLots, useLotDetails, useMultipleArticles } from '@gozoki/api'
import type { Lot } from '@gozoki/api-types'
import { Button, Chip, IconButton, Paper, TextField, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styles from '../../../assets/styles/QualityPage.module.css'
import Page from '../../../components/Page'
import { useToast } from '../../../components/communication/Toast'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import { getArticleColumnDef } from '../../../components/routes/quality/qualityUtils'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { formatDateOnly } from '../../../utils/types/formatDates'

export const Route = createFileRoute('/_authenticated/quality/$lot')({
  component: LotPage,
})

const LotStatus = ({ lot }: { lot: Lot }) => {
  let title = 'Non bloqué'
  if (lot.blocked) {
    title = 'Bloqué'
  }
  if (!lot.blocked && lot.blockedAt) {
    title = 'Débloqué'
  }
  return <Chip style={{ height: 35 }} label={title} color={lot.blocked ? 'error' : 'success'} />
}

// Use shared column definitions from qualityUtils
const articlesColumnDef = getArticleColumnDef()

function LotPage() {
  const [Toast, showToast] = useToast()
  const queryClient = useQueryClient()
  const { lot: lotId } = Route.useParams()

  const { data: lotDetails, isLoading: isLoadingLot } = useLotDetails(lotId)
  const [previousComment, setPreviousComment] = useState('')
  const [comment, setComment] = useState('')
  const [commentInitialized, setCommentInitialized] = useState(false)

  const navigate = Route.useNavigate()

  const { data: articles, isLoading: isLoadingArticles } = useMultipleArticles(lotDetails?.articles)

  // Initialize comment when lot details are first loaded (only once)
  useEffect(() => {
    if (lotDetails && !commentInitialized) {
      setComment(lotDetails.comment || '')
      setPreviousComment(lotDetails.comment || '')
      setCommentInitialized(true)
    }
  }, [lotDetails, commentInitialized])

  const commentHasChanged = useMemo(() => comment !== previousComment, [comment, previousComment])

  // Memoize the comment change handler
  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setComment(e.target.value)
  }, [])

  const { mutate: mutateBlockLot } = useMutation({
    mutationFn: handleBlockLots,
    mutationKey: ['blockLot', lotId],
    onError: (error) => {
      showToast({ message: error.message || 'Erreur', severity: 'error' })
    },
    onSuccess: () => {
      const message = lotDetails?.blocked ? 'Lot débloqué avec succès' : 'Lot bloqué avec succès'
      showToast({ message, severity: 'success' })
      // Invalidate lot details to update the status
      queryClient.invalidateQueries({ queryKey: ['quality', 'lotDetails', lotId] })
      // Invalidate lots list on Quality page so it refetches with updated data
      queryClient.invalidateQueries({ queryKey: ['quality', 'lotsData'] })
    },
  })

  const { mutate: mutateCommentLot } = useMutation({
    mutationFn: commentLots,
    mutationKey: ['commentLot', lotId],
    onError: (error) => {
      showToast({ message: error.message || 'Erreur', severity: 'error' })
    },
    onSuccess: () => {
      showToast({ message: 'Commentaire modifié avec succès', severity: 'success' })
      setPreviousComment(comment)
      // Invalidate lot details to update the comment
      queryClient.invalidateQueries({ queryKey: ['quality', 'lotDetails', lotId] })
      // Invalidate lots list on Quality page so it refetches with updated data
      queryClient.invalidateQueries({ queryKey: ['quality', 'lotsData'] })
    },
  })
  // This silent function is used when blocking a lot to avoid getting 2 toasts
  const { mutate: mutateCommentLotSilent } = useMutation({
    mutationFn: commentLots,
    mutationKey: ['commentLotSilent', lotId],
    onError: (error) => {
      showToast({ message: error.message || 'Erreur', severity: 'error' })
    },
    onSuccess: () => {
      setPreviousComment(comment)
      // Don't invalidate here - the block mutation will handle it
    },
  })

  // Memoize header info array to avoid recreating on every render
  const headerInfo = useMemo(() => {
    if (!lotDetails) {
      return []
    }
    return [
      { title: 'N° de lot', value: lotDetails.lot },
      { title: 'EAN 13', value: lotDetails.ean },
      { title: 'Libellé', value: lotDetails.label },
      { title: 'Origine', value: lotDetails.tracking },
      {
        title: 'DLC',
        value: formatDateOnly(lotDetails.dlc),
      },
      { title: "Nb d'articles", value: lotDetails.nb },
    ]
  }, [lotDetails])

  // Memoize articles rows to avoid recreating on every render
  const articleRows = useMemo(() => {
    if (!articles) {
      return []
    }
    return articles.map((a) => ({
      boutiqueAt: a.boutiqueAt,
      checkPointArticleRef: a.checkpointArticleRef,
      destroyedAt: a.state === 'DESTROYED' ? a.lostAt : null,
      id: a.checkpointArticleRef,
      shippingAt: a.shippingAt,
      soldAt: a.state === 'SOLD' ? a.lostAt : null,
      stolenAt: a.state === 'STOLEN' ? a.lostAt : null,
      storeLabel: a.store.label,
      warehouseAt: a.warehouseAt,
    }))
  }, [articles])

  if (isLoadingLot) {
    return (
      <Page>
        <Toast />
        <div style={{ alignItems: 'center', display: 'flex', gap: 8, paddingLeft: 10 }}>
          <IconButton onClick={() => navigate({ to: '/quality' })}>
            <ArrowLeft
              style={{
                color: ThemeColors.BLACK,
                height: '24px',
                width: '24px',
              }}
            />
          </IconButton>
          <Typography style={fonts.pageTitle} flexGrow={1}>
            Détails du lot
          </Typography>
        </div>
        <Typography style={{ padding: 20 }}>Chargement...</Typography>
      </Page>
    )
  }

  if (!lotDetails) {
    return (
      <Page>
        <Toast />
        <div style={{ alignItems: 'center', display: 'flex', gap: 8, paddingLeft: 10 }}>
          <IconButton onClick={() => navigate({ to: '/quality' })}>
            <ArrowLeft
              style={{
                color: ThemeColors.BLACK,
                height: '24px',
                width: '24px',
              }}
            />
          </IconButton>
          <Typography style={fonts.pageTitle} flexGrow={1}>
            Détails du lot
          </Typography>
        </div>
        <Typography style={{ padding: 20 }}>Lot non trouvé</Typography>
      </Page>
    )
  }

  return (
    <Page>
      <Toast />
      <div style={{ alignItems: 'center', display: 'flex', gap: 8, paddingLeft: 10 }}>
        <IconButton onClick={() => navigate({ to: '/quality' })}>
          <ArrowLeft
            style={{
              color: ThemeColors.BLACK,
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Détails du lot
        </Typography>
      </div>

      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div className={styles.headerPaper}>
          {headerInfo.map((item, index) => (
            <div key={index} style={{ flex: 1 }}>
              <p style={fonts.inputSubtitle}>{item.title}</p>
              <div style={{ padding: 6 }} />
              <p style={fonts.inputHeader}>{item.value}</p>
            </div>
          ))}
        </div>
      </Paper>

      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div className={styles.contentPaper}>
          <div className={styles.detailsLine}>
            <div style={{ flex: 1 }} className={styles.detailsLinePart}>
              <p style={fonts.inputHeader}>Statut</p>
            </div>
            <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
              <LotStatus lot={lotDetails} />
            </div>
          </div>
          <div className={styles.detailsLine}>
            <div style={{ flex: 1 }} className={styles.detailsLinePart}>
              <p style={fonts.inputHeader}>Commentaire</p>
            </div>
            <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
              <TextField
                variant="outlined"
                placeholder="Commentaire"
                fullWidth
                value={comment}
                onChange={handleCommentChange}
              />
            </div>
          </div>
          <div className={styles.detailsLine}>
            <div style={{ flex: 1 }} className={styles.detailsLinePart}>
              <p style={fonts.inputHeader}>Bloqué à</p>
            </div>
            <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
              {lotDetails.blockedAt ? (
                <p>
                  {new Date(lotDetails.blockedAt).toLocaleDateString('fr-FR')}
                  {' - '}
                  {new Date(lotDetails.blockedAt).toLocaleTimeString('fr-FR')}
                </p>
              ) : (
                <p>-</p>
              )}
            </div>
          </div>
          <div className={styles.detailsLine}>
            <div style={{ flex: 1 }} className={styles.detailsLinePart}>
              <p style={fonts.inputHeader}>Débloqué à</p>
            </div>
            <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
              {lotDetails.unBlockedAt ? (
                <p>
                  {new Date(lotDetails.unBlockedAt).toLocaleDateString('fr-FR')}
                  {' - '}
                  {new Date(lotDetails.unBlockedAt).toLocaleTimeString('fr-FR')}
                </p>
              ) : (
                <p>-</p>
              )}
            </div>
          </div>
          <div className={styles.detailsLine}>
            <div style={{ flex: 1 }} className={styles.detailsLinePart}>
              <p style={fonts.inputHeader}>Bloqué par</p>
            </div>
            <div style={{ flex: 2 }} className={styles.detailsLinePartTwo}>
              {lotDetails.blockedBy ? <p>{lotDetails.blockedBy}</p> : <p>-</p>}
            </div>
          </div>
        </div>
        <div className={styles.headerPaper} style={{ justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="info"
            style={{ margin: 10 }}
            disabled={!commentHasChanged}
            onClick={() => {
              // Require comment when batch is blocked
              if (lotDetails.blocked && !comment.trim()) {
                showToast({
                  message: 'Un lot bloqué doit obligatoirement avoir un commentaire',
                  severity: 'error',
                })
                return
              }
              mutateCommentLot({ comment, ids: lotDetails.articles })
            }}
          >
            Modifier le commentaire
          </Button>
          <Button
            variant="contained"
            color={lotDetails.blocked ? 'success' : 'error'}
            onClick={() => {
              // Require comment when blocking
              if (!lotDetails.blocked && !comment.trim()) {
                showToast({
                  message: 'Veuillez ajouter un commentaire avant de bloquer le lot',
                  severity: 'error',
                })
                return
              }
              // Save comment if it has changed (use silent mutation)
              if (commentHasChanged) {
                mutateCommentLotSilent({ comment, ids: lotDetails.articles })
              }
              mutateBlockLot(lotDetails.articles)
            }}
            style={{ margin: 10 }}
          >
            {lotDetails.blocked ? 'Débloquer' : 'Bloquer'}
          </Button>
        </div>
      </Paper>

      {articleRows.length > 0 ? (
        <Paper
          sx={{
            marginTop: '16px',
          }}
        >
          <SimpleDataGrid
            columns={articlesColumnDef}
            rows={articleRows}
            loading={isLoadingArticles}
            disableRowSelectionOnClick
            checkboxSelection={false}
          />
        </Paper>
      ) : null}
    </Page>
  )
}
